import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';

const { FiMenu, FiX, FiPhone, FiMail, FiClock } = FiIcons;
const { FaWhatsapp, FaFacebook } = FaIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const whatsappUrl = "https://wa.me/447734253236";
  const facebookUrl = "https://www.facebook.com/share/1Am41wyJwq/";

  return (
    <>
      {/* Top contact bar - Desktop Only */}
      <div className="hidden lg:block bg-blue-900 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm font-medium">
          <div className="flex gap-6">
            <a href="tel:07734253236" className="flex items-center gap-2 hover:text-red-400 transition-colors">
              <SafeIcon icon={FiPhone} className="w-4 h-4 text-red-500" />
              <span>07734253236</span>
            </a>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-green-400 transition-colors">
              <SafeIcon icon={FaWhatsapp} className="w-4 h-4 text-green-500" />
              <span>WhatsApp</span>
            </a>
            <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
              <SafeIcon icon={FaFacebook} className="w-4 h-4 text-blue-500" />
              <span>Facebook</span>
            </a>
            <a href="mailto:Apex.Aerials@aol.com" className="flex items-center gap-2 hover:text-red-400 transition-colors">
              <SafeIcon icon={FiMail} className="w-4 h-4 text-red-500" />
              <span>Email</span>
            </a>
          </div>
          <div className="flex items-center gap-4 text-xs opacity-90">
            <span className="flex items-center gap-1">
              <SafeIcon icon={FiClock} className="w-3 h-3" />
              Open 7 Days: 8am - 9pm
            </span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg py-2' : 'bg-white py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12 md:h-16">
            <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('home')}>
              <img 
                src="https://i.postimg.cc/Kv8cK38J/A078C18F-42A2-4F90-8BD2-98D500BE7B94.png" 
                alt="Apex Aerials" 
                className="h-8 md:h-12 w-auto transition-all" 
              />
            </div>

            <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
              {['home', 'about', 'services', 'gallery', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className="text-gray-700 hover:text-red-600 px-2 py-1 font-semibold capitalize transition-colors text-sm lg:text-base relative group"
                >
                  {item}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all group-hover:w-full"></span>
                </button>
              ))}
              <a 
                href="tel:01617945556"
                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-full font-bold transition-all shadow-md hover:shadow-lg text-sm"
              >
                Call Office
              </a>
            </nav>

            <div className="flex md:hidden items-center">
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-900 p-2 rounded-lg bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-0 z-50 md:hidden bg-white"
            >
              <div className="flex flex-col h-full">
                <div className="flex justify-between items-center p-6 border-b">
                  <img 
                    src="https://i.postimg.cc/Kv8cK38J/A078C18F-42A2-4F90-8BD2-98D500BE7B94.png" 
                    alt="Logo" 
                    className="h-8 w-auto"
                  />
                  <button onClick={() => setIsMenuOpen(false)} className="p-2 text-gray-500">
                    <SafeIcon icon={FiX} className="w-8 h-8" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-8">
                  <div className="space-y-4">
                    {['home', 'about', 'services', 'gallery', 'contact'].map((item) => (
                      <button
                        key={item}
                        onClick={() => scrollToSection(item)}
                        className="block w-full text-left text-2xl font-bold text-gray-900 capitalize py-2"
                      >
                        {item}
                      </button>
                    ))}
                  </div>

                  <div className="mt-12 pt-8 border-t border-gray-100 space-y-6">
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Connect With Us</p>
                      <div className="space-y-4">
                        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                          <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center shadow-md">
                            <SafeIcon icon={FaWhatsapp} className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">WhatsApp</p>
                            <p className="text-lg font-bold text-gray-900">07734253236</p>
                          </div>
                        </a>
                        <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                          <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-md">
                            <SafeIcon icon={FaFacebook} className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Facebook</p>
                            <p className="text-lg font-bold text-gray-900">Visit Page</p>
                          </div>
                        </a>
                        <a href="tel:07734253236" className="flex items-center gap-4 group">
                          <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center shadow-md">
                            <SafeIcon icon={FiPhone} className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Mobile (24/7)</p>
                            <p className="text-lg font-bold text-gray-900">07734253236</p>
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Floating Buttons */}
      <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-3 md:hidden">
        <motion.a
          href="tel:07734253236"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1 }}
          className="bg-red-600 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-transform"
          aria-label="Call Now"
        >
          <SafeIcon icon={FiPhone} className="w-6 h-6" />
        </motion.a>
        
        <motion.a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="bg-green-500 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-transform"
          aria-label="WhatsApp Us"
        >
          <SafeIcon icon={FaWhatsapp} className="w-7 h-7" />
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
          </span>
        </motion.a>
      </div>
    </>
  );
};

export default Header;